import { Inject, Injectable, Injector, NgZone, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { NAVIGATOR, SESSION_STORAGE } from '@ng-web-apis/common';
import { I18NextService, ITranslationService } from 'angular-i18next';
import { BehaviorSubject, Subscription, catchError, from, mergeMap, of, retry, take, tap, throwError } from 'rxjs';
import { GoogleMapService } from 'src/app/_shared/components/google-map/google-map.service';
import { SearchService } from 'src/app/_shared/components/prefix-search/search.service';
import { RouteTranslateService } from 'src/app/common/route-translate/route-translate.service';
import { GoogleAnalyticsService } from '../analytics/google-analytics.service';
import { OpenDialogService } from '../open-dialog/open-dialog.service';
import { getCurrentPosition } from './current-position';

@Injectable({ providedIn: 'root', deps: [GoogleMapService] })
export class LocationSearchService implements OnDestroy {
  loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private apiLoaded: boolean;
  private apiSubscription = new Subscription();
  private geolocationSubscription = new Subscription();
  private deniedThisSession = false;

  constructor(
    private routeTranslateService: RouteTranslateService,
    @Inject(I18NextService) private translateService: ITranslationService,
    private injector: Injector,
    private ngZone: NgZone,
    private router: Router,
    private openDialogService: OpenDialogService,
    private searchService: SearchService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private googleMapService: GoogleMapService,
    private matSnackBar: MatSnackBar,
    @Inject(SESSION_STORAGE) private sessionStorage: Storage,
    @Inject(NAVIGATOR) private navigator: Navigator,
  ) {}

  onSearch(types: string[] = [], searchQualifications: string[] = [], withUsers?: boolean) {
    if (this.apiLoaded) {
      this.geolocationSubscription.unsubscribe();
      this.sessionStorage.removeItem('previousSearch');
      this.sessionStorage.removeItem('previousSearch__map');
      this.sessionStorage.removeItem('mobileSearchPageValue');
      let latitude: number;
      let longitude: number;
      let askingNow = false;
      let loadingShown = false;
      const showLoading = () => {
        if (loadingShown) return;
        loadingShown = true;
        this.ngZone.run(() => this.loading.next(true));
      };
      this.geolocationSubscription = from(this.permissionState())
        .pipe(
          mergeMap(state => {
            askingNow = state === 'prompt' && !this.deniedThisSession;
            if (state === 'granted' || state === 'unknown') showLoading();
            return getCurrentPosition(this.navigator);
          }),
          tap(() => {
            this.deniedThisSession = false;
            showLoading();
          }),
          catchError((err: GeolocationPositionError) => throwError(() => err)),
          mergeMap(position => {
            latitude = position.coords.latitude;
            longitude = position.coords.longitude;
            return from(import('@angular/google-maps')).pipe(
              mergeMap(({ MapGeocoder }) =>
                this.injector.get(MapGeocoder).geocode({
                  location: {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                  },
                }),
              ),
            );
          }),
        )
        .subscribe({
          next: response => {
            this.searchService.setSearchRefresh(true);
            this.router.navigate([this.routeTranslateService.translateRoute('search-results')], {
              queryParams: {
                prefix: 'location',
                types,
                searchQualifications,
                withUsers,
                mode: 'list',
                interaction: 'geoLocation',
                latitude,
                longitude,
                distance: 50,
                value:
                  response.results[0].address_components?.filter(
                    c =>
                      !c.types.includes('street_address') &&
                      !c.types.includes('route') &&
                      !c.types.includes('street_number') &&
                      !c.types.includes('postal_code'),
                  )[0].long_name +
                  ', ' +
                  response.results[0].address_components?.filter(c =>
                    c.types.includes('administrative_area_level_2'),
                  )[0].short_name,
              },
              state: { refresh: true },
            });
            this.ngZone.run(() => {
              this.loading.next(false);
              this.googleAnalyticsService.logCustomEvent('near_by', 'NearByBtn');
            });
          },
          error: (err: GeolocationPositionError | any) => {
            const data = {
              permissionDenied: err?.code === 1 || /denied|permission/i.test(String(err?.message ?? '')),
            };
            this.ngZone.run(() => this.loading.next(false));
            if (data.permissionDenied) {
              this.deniedThisSession = true;
              if (askingNow) return;
            }
            import('../../_shared/dialogs/location-search-dialog/location-search-dialog.component').then(
              ({ LocationSearchDialogComponent }) =>
                this.ngZone.run(() => {
                  const dialogRef = this.openDialogService.openDialog(LocationSearchDialogComponent, {
                    panelClass: 'sharry-alert-container',
                    data,
                  });
                  if (data.permissionDenied) {
                    this.retryWhenAllowed(dialogRef, () => this.onSearch(types, searchQualifications, withUsers));
                  }
                }),
            );
          },
        });
    } else {
      this.apiSubscription = this.googleMapService.apiLoaded
        .pipe(
          mergeMap(loaded => {
            if (loaded) {
              return of({ loaded, error: false });
            } else {
              return throwError(() => new Error());
            }
          }),
          retry({
            count: 5,
            delay: 500,
            resetOnSuccess: true,
          }),
          catchError(() => of({ error: true, loaded: false })),
          take(1),
        )
        .subscribe({
          next: r => {
            this.apiLoaded = r.loaded;
            if (r.loaded) {
              this.onSearch(types, searchQualifications, withUsers);
            } else {
              this.matSnackBar.open(this.translateService.t('location-search.error.subtitle'), '', {
                duration: 3000,
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
              });
            }
          },
        });
      this.googleMapService.loadApi();
    }
  }

  private async permissionState(): Promise<'granted' | 'denied' | 'prompt' | 'unknown'> {
    try {
      if (Capacitor.isNativePlatform()) {
        const { location, coarseLocation } = await Geolocation.checkPermissions();
        if (location === 'granted' || coarseLocation === 'granted') return 'granted';
        if (location === 'denied' && coarseLocation === 'denied') return 'denied';
        return 'prompt';
      }
      if (!this.navigator.permissions?.query) return 'unknown';
      return (await this.navigator.permissions.query({ name: 'geolocation' })).state;
    } catch {
      return 'unknown';
    }
  }

  private retryWhenAllowed(dialogRef: MatDialogRef<unknown> | undefined, retry: () => void) {
    if (!dialogRef) return;
    if (Capacitor.isNativePlatform()) {
      this.retryOnResumeWhenAllowed(dialogRef, retry);
      return;
    }
    if (!this.navigator.permissions?.query) return;
    this.navigator.permissions
      .query({ name: 'geolocation' })
      .then(status => {
        const onChange = () => {
          if (status.state === 'denied') return;
          status.removeEventListener('change', onChange);
          this.ngZone.run(() => {
            dialogRef.close();
            retry();
          });
        };
        status.addEventListener('change', onChange);
        dialogRef.afterClosed().subscribe(() => status.removeEventListener('change', onChange));
      })
      .catch(() => undefined);
  }

  private retryOnResumeWhenAllowed(dialogRef: MatDialogRef<unknown>, retry: () => void) {
    let closed = false;
    const listener = App.addListener('resume', () => {
      Geolocation.checkPermissions()
        .then(({ location, coarseLocation }) => {
          if (closed || (location !== 'granted' && coarseLocation !== 'granted')) return;
          this.ngZone.run(() => {
            dialogRef.close();
            retry();
          });
        })
        .catch(() => undefined);
    });
    dialogRef.afterClosed().subscribe(() => {
      closed = true;
      listener.then(handle => handle.remove()).catch(() => undefined);
    });
  }

  ngOnDestroy(): void {
    this.apiSubscription.unsubscribe();
    this.geolocationSubscription.unsubscribe();
  }
}
